import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { addYears, isAfter, isSameDay, startOfDay } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { OneTimeInStagesModel, StagesTerms, StagesValueType } from '~/models/OneTimeInStagesModel';
import { RecurrencePeriodTypes } from '~/models/service-billing';
import { ServiceCostType } from '~/models/service-cost';
import { fromDateStampObject } from '~/models/time';
import { BillingTriggerType } from '~/types/api.model';
import { utils } from '~/utils';
export const billingTriggersTextsPath = 'billing.triggers';
export const billingStagesTextsPath = 'billing.stages';
export const billingCreditsTextsPath = 'billing.credits';
export const billingInitiationTextsPath = 'billing.initiation';
export function makeInvoiceDateInfo(invoiceDraftDates, selectedDate) {
    if (!invoiceDraftDates.dates().length) {
        throw new Error('futureInvoicesDates.length is 0');
    }
    const actualSelectedDate = selectedDate || invoiceDraftDates.dates()[0];
    return {
        invoiceDraftDates,
        selectedDate: actualSelectedDate,
        isUpcomingSelected: isSameDay(actualSelectedDate, invoiceDraftDates.dates()[0])
    };
}
export function getCreditsWarning(totalCredits, t) {
    if (totalCredits.gt(0)) return {
        text: t.warning({
            amount: utils.formatDollarAmount(totalCredits)
        })
    };
    return null;
}
export const getMaxFutureInvoiceDate = (now)=>addYears(startOfDay(now), 1);
export function isFutureEffectiveDate() {
    let effectiveDate = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : null, now = arguments.length > 1 ? arguments[1] : void 0;
    const normalizedEffectiveDate = effectiveDate && (effectiveDate instanceof Date ? effectiveDate : fromDateStampObject(effectiveDate));
    return !!(normalizedEffectiveDate && now && isAfter(startOfDay(normalizedEffectiveDate), startOfDay(now)));
}
export function serviceBillingTriggerOptions(param) {
    let { t, withEffectiveDate, recurrencePeriod, pricingType, billingTrigger, isFromAgreement, isExistingService, featureFlags } = param;
    const { YEARLY_AUTOMATIC_SERVICE, BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE } = featureFlags;
    const isOnDemand = billingTrigger === BillingTriggerType.OnDemand;
    const isOneTime = !recurrencePeriod;
    const isYearly = recurrencePeriod === RecurrencePeriodTypes.Yearly;
    const isVariablePrice = pricingType === ServiceCostType.Variable;
    const isFixedPrice = pricingType === ServiceCostType.Fixed;
    const isRangePrice = pricingType === ServiceCostType.Range;
    const isOneTimeManual = !recurrencePeriod && billingTrigger === BillingTriggerType.OneTimeManual;
    const isAmendment = isExistingService && isFromAgreement;
    const isEffectiveDateInFuture = isFutureEffectiveDate(withEffectiveDate === null || withEffectiveDate === void 0 ? void 0 : withEffectiveDate.effectiveDate, withEffectiveDate === null || withEffectiveDate === void 0 ? void 0 : withEffectiveDate.now);
    const isDisabledForOngoingYearly = !isOneTime && isYearly && !YEARLY_AUTOMATIC_SERVICE;
    const getBilledEffectiveDateTexts = ()=>{
        if (isEffectiveDateInFuture) {
            return {
                text: t.automatic.onEffectiveDate.text(),
                subtext: t.automatic.onEffectiveDate.subtext()
            };
        }
        if (isFromAgreement) {
            return {
                text: t.automatic.immediately.text(),
                subtext: t.automatic.immediately.subtext()
            };
        }
        return {
            text: t.automatic.onProposalAcceptance.text(),
            subtext: t.automatic.onProposalAcceptance.subtext()
        };
    };
    return compact([
        isFixedPrice && isOneTime && !isOnDemand && !(isAmendment && isOneTimeManual) && _object_spread({
            value: 'approval'
        }, getBilledEffectiveDateTexts()),
        !isVariablePrice && !isRangePrice && !isOneTime && (YEARLY_AUTOMATIC_SERVICE || !isYearly || BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE) && {
            disabled: isDisabledForOngoingYearly,
            disabledTooltip: isDisabledForOngoingYearly ? t.tooltips.yearlyServicesManualOnly() : undefined,
            value: 'ongoing',
            text: t.automatic.onSchedule.text(),
            subtext: t.automatic.onSchedule.subtext()
        },
        isFixedPrice && {
            value: 'manualFixed',
            text: t.manual.fixed.text(),
            subtext: t.manual.fixed.subtext()
        },
        !isFixedPrice && {
            value: 'manualVariable',
            text: t.manual.variable.text(),
            subtext: t.manual.variable.subtext()
        }
    ]);
}
export const stagesTermsTextMap = (t)=>({
        [StagesTerms.AutoFirst]: t.terms.autoFirst.text(),
        [StagesTerms.AllManual]: t.terms.allManual.text()
    });
export function serviceBillingStagesTermsOptions(param) {
    let { t, now, agreementEffectiveDate, isFreshlyAddedToApprovedAgreement } = param;
    const autoInvoiceIssued = isFreshlyAddedToApprovedAgreement ? t.invoiceIssuedTiming.onAmendmentsSaved() : isFutureEffectiveDate(agreementEffectiveDate, now) ? t.invoiceIssuedTiming.onEffectiveDate() : t.invoiceIssuedTiming.onAgreementApproval();
    return [
        {
            value: StagesTerms.AutoFirst,
            text: t.terms.autoFirst.text(),
            subtext: t.terms.autoFirst.subtext({
                autoInvoiceIssued
            })
        },
        {
            value: StagesTerms.AllManual,
            text: t.terms.allManual.text(),
            subtext: t.terms.allManual.subtext()
        }
    ];
}
export const makeInitialOneTimeInStages = ()=>OneTimeInStagesModel.make({
        valueType: StagesValueType.Percent,
        terms: StagesTerms.AutoFirst,
        stages: [
            OneTimeInStagesModel.makeStage(1, new Decimal(50)),
            OneTimeInStagesModel.makeStage(2, new Decimal(50))
        ]
    });
export function hasYearlyAutomaticBillingError(service, featureFlags) {
    const { YEARLY_AUTOMATIC_SERVICE } = featureFlags;
    const isYearly = service.billing.recurrencePeriod === RecurrencePeriodTypes.Yearly;
    const isAutomatic = service.billing.trigger === BillingTriggerType.Ongoing || service.billing.trigger === BillingTriggerType.OnApproval;
    return isYearly && !YEARLY_AUTOMATIC_SERVICE && isAutomatic;
}
